
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { Logo } from '@/components/ui/logo';
import { CircleDecor } from '@/components/ui/visual-elements';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

const resetPasswordSchema = z.object({
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  confirmPassword: z.string().min(6, 'Confirme sua senha')
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não correspondem",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validResetToken, setValidResetToken] = useState(false);
  
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    }
  });
  
  // Verificar se há um token válido de redefinição de senha na URL
  useEffect(() => {
    const checkResetToken = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        if (data.session) {
          setValidResetToken(true);
        } else {
          // Analisar a URL em busca do token de recuperação
          const hash = window.location.hash.substring(1);
          const params = new URLSearchParams(hash);
          const accessToken = params.get('access_token');
          const type = params.get('type');
          
          if (accessToken && type === 'recovery') {
            setValidResetToken(true);
          } else {
            toast({
              title: "Link inválido",
              description: "O link de redefinição de senha é inválido ou expirou.",
              variant: "destructive",
            });
            setTimeout(() => navigate('/login'), 3000);
          }
        }
      } catch (error: any) {
        console.error("Erro ao verificar token de redefinição:", error.message);
        toast({
          title: "Erro ao verificar token",
          description: error.message || "Não foi possível verificar o token de redefinição.",
          variant: "destructive",
        });
        setTimeout(() => navigate('/login'), 3000);
      }
    };
    
    checkResetToken();
  }, [navigate]);
  
  const onSubmit = async (values: ResetPasswordFormValues) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.updateUser({
        password: values.password
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Senha redefinida",
        description: "Sua senha foi atualizada com sucesso.",
      });
      
      // Redirecionar para a página de login após a redefinição bem-sucedida
      setTimeout(() => navigate('/login'), 2000);
    } catch (error: any) {
      console.error("Erro ao redefinir senha:", error.message);
      toast({
        title: "Erro ao redefinir senha",
        description: error.message || "Não foi possível redefinir sua senha. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-hidden">
      <CircleDecor className="absolute -top-10 -right-10 opacity-30" size="lg" />
      <CircleDecor className="absolute bottom-10 left-10 opacity-30" variant="secondary" />
      
      <div className="flex flex-col items-center justify-center w-full p-8 relative z-10">
        <div className="text-center mb-8">
          <Logo className="mx-auto mb-4" size="lg" />
          <p className="text-gray-600">redefinir senha</p>
        </div>
        
        <div className="w-full max-w-md">
          {validResetToken ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>nova senha</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input 
                            type={showPassword ? "text" : "password"} 
                            placeholder="sua nova senha" 
                            {...field} 
                          />
                          <button 
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOffIcon className="h-5 w-5 text-gray-400" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>confirmar senha</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input 
                            type={showConfirmPassword ? "text" : "password"} 
                            placeholder="confirme sua nova senha" 
                            {...field} 
                          />
                          <button 
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? (
                              <EyeOffIcon className="h-5 w-5 text-gray-400" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-brand-blue hover:bg-opacity-90 text-white"
                  disabled={loading}
                >
                  {loading ? "Redefinindo..." : "redefinir senha"}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="text-center">
              <p>Verificando link de redefinição de senha...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
